import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, MapPin, Wrench } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema, localBusinessSchema } from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Btn, Overline } from "@/components/Btn";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CtaBand } from "@/components/CtaBand";
import { getLocale, LOCALES } from "@/data/locales";
import { getService } from "@/data/services";
import { getRealisation, REALISATIONS } from "@/data/realisations";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function LocalPage() {
  const { slug } = useParams();
  const l = getLocale(slug);
  if (!l) return <Navigate to="/404" replace />;

  const path = `/${l.slug}/`;
  const crumbs = [
    { label: "Accueil", to: "/" },
    { label: "Zones d'intervention", to: "/zones-intervention/" },
    { label: l.zoneLabel, to: `/zones-intervention/` },
    { label: l.nav, to: path },
  ];

  const nearbyLocales = l.nearby.map(getLocale).filter(Boolean);
  const zoneRealisations = REALISATIONS.filter((r) => r.zoneKey === l.zoneKey);

  return (
    <>
      <Seo
        title={l.title}
        description={l.metaDescription}
        path={path}
        image={l.image}
        jsonLd={[localBusinessSchema(), breadcrumbSchema(crumbs), faqSchema(l.faq)]}
      />

      <section className="relative overflow-hidden border-b border-white/10 bg-ink-900" data-testid="local-hero">
        <div className="absolute inset-0 z-0">
          <img src={l.image} alt={l.h1} className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/80 to-ink-900/55" />
          <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <Breadcrumb items={crumbs} />
          <Reveal className="mt-8 max-w-3xl">
            <Overline>{l.overline}</Overline>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white leading-[0.98]">{l.h1}</h1>
            {l.intro.map((p, i) => (
              <p key={i} className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">{p}</p>
            ))}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Btn to="/contact/" size="lg" data-testid="local-devis-btn">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
              <Btn href="tel:+33610443864" variant="outline" size="lg">Nous appeler</Btn>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Un marché local, une réponse adaptée</h2>
              <p className="mt-4 text-slate-300 leading-relaxed">{l.localContext}</p>
            </Reveal>

            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3"><Wrench className="h-6 w-6 text-signature" /> Nos services dans ce secteur</h2>
              <Stagger className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {l.services.map((sl) => {
                  const svc = getService(sl);
                  if (!svc) return null;
                  return (
                    <StaggerItem key={sl}>
                      <Link to={`/${svc.slug}/`} className="group flex items-center justify-between rounded-xl border border-white/10 bg-ink-800 p-5 hover:border-signature/40 transition-all" data-testid={`local-service-${sl}`}>
                        <div>
                          <h3 className="text-base font-semibold text-white group-hover:text-signature transition-colors">{svc.nav}</h3>
                          <p className="mt-1 text-xs text-slate-400 line-clamp-1">{svc.overline}</p>
                        </div>
                        <ArrowUpRight className="h-5 w-5 text-chrome-mid group-hover:text-signature transition-colors shrink-0" />
                      </Link>
                    </StaggerItem>
                  );
                })}
              </Stagger>
            </Reveal>

            {zoneRealisations.length > 0 && (
              <Reveal>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">Réalisations dans la région</h2>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {zoneRealisations.slice(0, 2).map((r) => (
                    <Link key={r.slug} to={`/realisations/${r.slug}/`} className="group block overflow-hidden rounded-xl border border-white/10 bg-ink-800 hover:border-signature/40 transition-all" data-testid={`local-realisation-${r.slug}`}>
                      <div className="relative h-44 overflow-hidden">
                        <img src={r.cover} alt={r.alt} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/90 to-transparent" />
                      </div>
                      <div className="p-5">
                        <p className="text-xs font-mono uppercase tracking-wider text-chrome-mid">{r.city}</p>
                        <h3 className="mt-1 text-base font-semibold text-white group-hover:text-signature transition-colors line-clamp-2">{r.title}</h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </Reveal>
            )}

            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5">Questions fréquentes</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {l.faq.map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`} className="rounded-xl border border-white/10 bg-ink-800 px-5" data-testid={`local-faq-${i}`}>
                    <AccordionTrigger className="text-left text-white hover:no-underline py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-400 pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="rounded-2xl border border-signature/30 bg-gradient-to-b from-ink-800 to-ink-900 p-7 glow-red">
                <h3 className="text-xl font-bold text-white">Projet à {l.zoneLabel} ?</h3>
                <p className="mt-3 text-sm text-slate-300">Nous intervenons près de chez vous, de l'étude à la pose.</p>
                <Btn to="/contact/" className="mt-5 w-full">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
              </div>

              <div className="rounded-2xl border border-white/10 bg-ink-800 p-7">
                <p className="text-xs font-mono uppercase tracking-wider text-signature mb-4">Zones proches</p>
                <ul className="space-y-1">
                  {nearbyLocales.map((n) => (
                    <li key={n.slug}>
                      <Link to={`/${n.slug}/`} className="group flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                        <MapPin className="h-4 w-4 text-chrome-dark group-hover:text-signature" /> {n.nav}
                      </Link>
                    </li>
                  ))}
                  <li><Link to="/zones-intervention/" className="flex items-center gap-1 px-3 py-2 text-sm text-white hover:text-signature">Toutes les zones <ArrowUpRight className="h-4 w-4" /></Link></li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <CtaBand title={`Votre enseigne à ${l.zoneLabel}`} />
    </>
  );
}
